<!-- previous MedicineList.vue -->
<template>
  <div class="medicine-list-view">
    <!-- Header with Cart and User Menu -->
    <div class="header">
      <h1>Order medicines from {{ selectedPharmacyName }}</h1>
      <!-- Menu Bar with Cart and Avatar -->
      <div class="menu-bar">
        <!-- Cart Icon with Badge -->
        <div class="menu-item cart-menu" @click="toggleDropdown('cart')">
          <div class="cart-icon-container">
            <svg class="cart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="m1 1 4 4 l0 13a5,5 0 0 0 5,5h9a5,5 0 0 0 5-5V9H8"></path>
            </svg>
            <span v-if="totalCartItems > 0" class="cart-badge">{{ totalCartItems }}</span>
          </div>
          <!-- Cart Dropdown -->
          <div class="dropdown-menu cart-dropdown" v-show="activeDropdown === 'cart'">
            <div v-if="cartItems.length === 0" class="cart-empty">
              <p>Your cart is empty</p>
            </div>
            <div v-else>
              <div class="cart-header">
                <h4>From {{ selectedPharmacyName }}</h4>
              </div>
              <div class="cart-header">
                <h4>Cart ({{ totalCartItems }} Qtys)</h4>
                <span class="cart-total">৳{{ cartTotal.toFixed(2) }}</span>
              </div>
              <div class="cart-items-list">
                <div v-for="item in cartItems" :key="item.medicine.id" class="cart-item">
                  <div class="cart-item-info">
                    <span class="medicine-name">{{ item.medicine.name }}</span>
                    <div class="cart-item-details">
                      <span class="quantity">Qty: {{ item.quantity }}</span>
                      <span class="price">৳{{ (parseFloat(item.medicine.tp_amount || 0) * item.quantity).toFixed(2) }}</span>
                    </div>
                  </div>
                  <button class="remove-item" @click="removeFromCartDropdown(item.medicine)" title="Remove from cart">
                    ✕
                  </button>
                </div>
              </div>
              <div class="cart-actions">
                <button class="clear-cart-btn" @click="clearCart">Clear Cart</button>
                <button class="checkout-btn" @click="proceedToCheckout">Checkout</button>
              </div>
            </div>
          </div>
        </div>

        <!-- User Avatar Menu -->
        <div class="menu-item" @click="toggleDropdown('menu')">
          <div class="user-avatar">
            <img src="/image/user_avater.png" alt="User Avatar" class="avatar-img" />
          </div>
          <div class="dropdown-menu menu-dropdown" v-show="activeDropdown === 'menu'">
            <div class="dropdown-item" @click.stop="navigateToMyOrders">
              <i class="icon-orders"></i>
              My Orders
            </div>
            <div class="dropdown-item" @click.stop="logout">
              <i class="icon-logout"></i>
              Logout
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Message -->
    <div v-if="showToast" class="toast-message" :class="toastType">
      {{ toastMessage }}
    </div>

    <div class="search-bar">
      <input type="text" placeholder="Search medicines..." v-model="searchQuery" />
    </div>

    <!-- Loading indicator -->
    <div class="loading-message" v-if="loading">
      <p>Loading medicines...</p>
    </div>

    <!-- Error message -->
    <div class="error-message" v-else-if="error">
      <p>{{ error }}</p>
      <button @click="fetchMedicines" class="load-all-button">Try Again</button>
    </div>

    <!-- No medicines message -->
    <div class="no-medicines" v-else-if="filteredMedicines.length === 0">
      <p>No medicines found matching your search.</p>
      <button @click="searchQuery = ''" class="load-all-button">Clear Search</button>
      <button @click="addItem = ''" class="add-item-button">Add Item</button>
    </div>

    <div class="medicine-grid" v-else>
      <div 
        v-for="medicine in displayedMedicines" 
        :key="medicine.id" 
        class="medicine-card"
        :class="{ 'in-cart': isInCart(medicine) }"
      >
        <div class="medicine-img">
          <div class="placeholder-img">💊</div>
        </div>
        <div class="medicine-info">
          <div class="medicine-actions">
          <h3>{{ medicine.name }}</h3>
          <!-- Cross/Delete Icon - Only show when item is in cart -->
            <button 
              v-if="isInCart(medicine) && getQuantity(medicine) > 0" 
              @click="removeFromCart(medicine)"
              class="delete-button"
              title="Remove from cart"
            >
            <img src="/image/delete_icon.png" alt="Delete" width="15" height="15">
            </button>
          </div>
          <div class="medicine-meta">
            <span class="medicine-subinfo">{{ medicine.generic }} || {{medicine.strength}} || {{ medicine.company }} </span>
            <span class="price"> ৳ {{ parseFloat(medicine.tp_amount || 0).toFixed(2) }}</span>
          </div>

          <div class="medicine-actions">
            <div class="left-actions">
              <div class="quantity-selector">
                <button @click="decreaseQuantity(medicine)">-</button>
                <input 
                  type="number" 
                  :value="getQuantity(medicine)" 
                  @input="updateQuantity(medicine, $event.target.value)"
                  @blur="handleQuantityBlur(medicine, $event.target.value)"
                  min="0"
                  class="quantity-input"
                />
                <button @click="increaseQuantity(medicine)">+</button>
              </div>
              <p class="medicine_quantity">{{medicine.pack_type}}</p>
              <p class="medicine_quantity_details"> 1 {{medicine.pack_type}}    {{medicine.pack_amount}}   {{ medicine.formation }}</p>  
            </div>
            
            <button class="add-to-cart" @click="addToCart(medicine)">
              <svg class="cart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="m1 1 4 4 l0 13a5,5 0 0 0 5,5h9a5,5 0 0 0 5-5V9H8"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      <!-- Loading more indicator -->
      <div class="loading-more" v-if="loadingMore">
        <p>Loading more medicines...</p>
      </div>
    </div>

    <!-- Footer -->
    <div class="footer">
      <p>© {{ new Date().getFullYear() }} Pharmacy App</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MedicineList',
  props: {
    selectedPharmacyId: {
      type: String,
      required: false
    },
    selectedPharmacyName: {
      type: String,
      required: false
    },
    selectedPharmacy: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      medicines: [],
      searchQuery: '',
      cartItems: [],
      loading: true,
      loadingMore: false,
      error: null,
      displayCount: 20,
      incrementCount: 10,
      localSelectedPharmacy: {},
      currentUser: {
        userId: '',
        userName: '',
        userMobile: '',
        userAddress: '',
        userEmail: '',
      },
      // Dropdown and toast functionality
      activeDropdown: null,
      showToast: false,
      toastMessage: '',
      toastType: 'info'
    }
  },
  computed: {
    filteredMedicines() {
      let filtered = this.medicines
  
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(medicine => 
          medicine.name.toLowerCase().includes(query) || 
          medicine.generic.toLowerCase().includes(query) ||
          medicine.company.toLowerCase().includes(query)
        )
      }
  
      return filtered
    },
    displayedMedicines() {
      return this.filteredMedicines.slice(0, this.displayCount)
    },
    totalCartItems() {
      return this.cartItems.reduce((total, item) => total + item.quantity, 0)
    },
    cartTotal() {
      return this.cartItems.reduce((total, item) => 
        total + (parseFloat(item.medicine.tp_amount || 0) * item.quantity), 0)
    }
  },
  methods: {
    // Toast message functionality
    showToastMessage(message, type = 'info') {
      this.toastMessage = message
      this.toastType = type
      this.showToast = true
      
      setTimeout(() => {
        this.showToast = false
      }, 3000)
    },

    // Dropdown functionality
    toggleDropdown(dropdown) {
      if (this.activeDropdown === dropdown) {
        this.activeDropdown = null
      } else {
        this.activeDropdown = dropdown
      }
    },

    // Close dropdowns when clicking outside
    closeDropdowns(event) {
      if (!event.target.closest('.menu-item')) {
        this.activeDropdown = null
      }
    },

    // Navigation methods
    navigateToMyOrders() {
      this.activeDropdown = null
      console.log('Navigating to My Orders page')
      this.$router.push({
        name: 'UserOrderHistory'
      })
    },

    logout() {
      this.activeDropdown = null
      console.log('User logging out')
      
      const keysToRemove = ['isLoggedIn', 'userId', 'userName', 'userMobile', 'userAddress', 'userEmail', 'status']
      keysToRemove.forEach(key => {
        localStorage.removeItem(key)
      })
      
      sessionStorage.clear()
      
      this.$router.push({
        name: 'UserLogin'
      })
    },

    // Cart functionality
    isInCart(medicine) {
      return this.cartItems.some(item => item.medicine.id === medicine.id)
    },

    removeFromCart(medicine) {
      this.cartItems = this.cartItems.filter(item => item.medicine.id !== medicine.id)
      this.updateCartInSession()
      console.log(`Removed ${medicine.name} from cart`)
    },

    // Remove from cart via dropdown
    removeFromCartDropdown(medicine) {
      this.removeFromCart(medicine)
      this.showToastMessage(`${medicine.name} removed from cart`, 'success')
    },

    clearCart() {
      if (this.cartItems.length === 0) {
        this.showToastMessage('Cart is already empty', 'info')
        return
      }
      
      if (confirm('Are you sure you want to clear your cart?')) {
        this.cartItems = []
        sessionStorage.removeItem('cartItems')
        sessionStorage.removeItem('selectedPharmacy')
        this.showToastMessage('Cart cleared successfully', 'success')
      }
    },

    proceedToCheckout() {
      if (this.cartItems.length === 0) {
        this.showToastMessage('Please add items to your cart first', 'warning')
        return
      }
      
      if (!this.checkAuthStatus()) {
        return
      }
      
      const verifiedCartItems = this.cartItems.map(item => {
        if (!item.medicine.tp_amount || isNaN(parseFloat(item.medicine.tp_amount))) {
          item.medicine.tp_amount = "0.00"
        }
        
        item.medicine.price = parseFloat(item.medicine.tp_amount || 0)
        return item
      })
      
      sessionStorage.setItem('cartItems', JSON.stringify(verifiedCartItems))
      
      const pharmacyData = {
        id: this.localSelectedPharmacy.id,
        name: this.localSelectedPharmacy.name,
        address: this.localSelectedPharmacy.address
      }
      sessionStorage.setItem('pharmacy', JSON.stringify(pharmacyData))
      sessionStorage.setItem('currentUser', JSON.stringify(this.currentUser))
      
      this.$router.push('/checkout')
    },

    // Handle infinite scroll
    handleScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.offsetHeight
      
      if (scrollTop + windowHeight >= documentHeight - 200) {
        this.loadMore()
      }
    },

    loadMore() {
      if (this.loadingMore || this.displayCount >= this.filteredMedicines.length) {
        return
      }
      
      this.loadingMore = true
      
      setTimeout(() => {
        this.displayCount += this.incrementCount
        this.loadingMore = false
      }, 500)
    },

    loadUserData() {
      this.currentUser = {
        userId: localStorage.getItem('userId') || '',
        userName: localStorage.getItem('userName') || '',
        userMobile: localStorage.getItem('userMobile') || '',
        userAddress: localStorage.getItem('userAddress') || '',
        userEmail: localStorage.getItem('userEmail') || ''
      }
      
      console.log('Current user data loaded in MedicineList:', this.currentUser)
    },
    
    checkAuthStatus() {
      const isLoggedIn = localStorage.getItem('isLoggedIn')
      if (isLoggedIn !== 'true' || !this.currentUser.userId) {
        console.log('User not logged in, redirecting to login')
        this.$router.push('/login')
        return false
      }
      return true
    },

    fetchMedicines() {
      this.loading = true
      this.error = null
      
      fetch('https://e01.yeapps.com/medi360/medicine_list')
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok')
          }
          return response.json()
        })
        .then(data => {
          if (data.status === 'success') {
            this.medicines = data.medicine_list.map(medicine => {
              return {
                ...medicine,
                tp_amount: medicine.tp_amount ? String(medicine.tp_amount) : "0.00"
              }
            })
            console.log('Medicines loaded:', this.medicines.length)
          } else {
            throw new Error(data.message || 'Failed to load medicines')
          }
        })
        .catch(error => {
          console.error('Error fetching medicines:', error)
          this.error = `Failed to load medicines: ${error.message}`
        })
        .finally(() => {
          this.loading = false
        })
    },
    
    getQuantity(medicine) {
      const cartItem = this.cartItems.find(item => item.medicine.id === medicine.id)
      return cartItem ? cartItem.quantity : 0
    },

    updateQuantity(medicine, value) {
      const quantity = parseInt(value) || 0
      
      if (quantity < 0) return
      
      const existingItem = this.cartItems.find(item => item.medicine.id === medicine.id)
      
      if (quantity === 0) {
        if (existingItem) {
          this.cartItems = this.cartItems.filter(item => item.medicine.id !== medicine.id)
        }
      } else {
        if (existingItem) {
          existingItem.quantity = quantity
        } else {
          this.cartItems.push({
            medicine: medicine,
            quantity: quantity
          })
        }
      }
      
      this.updateCartInSession()
    },

    handleQuantityBlur(medicine, value) {
      const quantity = parseInt(value) || 0
      this.updateQuantity(medicine, quantity)
    },
    
    increaseQuantity(medicine) {
      const existingItem = this.cartItems.find(item => item.medicine.id === medicine.id)
      
      if (existingItem) {
        existingItem.quantity++
      } else {
        this.cartItems.push({
          medicine: medicine,
          quantity: 1
        })
      }
      
      this.updateCartInSession()
    },
    
    decreaseQuantity(medicine) {
      const existingItem = this.cartItems.find(item => item.medicine.id === medicine.id)
      
      if (existingItem && existingItem.quantity > 0) {
        existingItem.quantity--
        
        if (existingItem.quantity === 0) {
          this.cartItems = this.cartItems.filter(item => item.medicine.id !== medicine.id)
        }
      }
      
      this.updateCartInSession()
    },
    
    addToCart(medicine) {
      const existingItem = this.cartItems.find(item => item.medicine.id === medicine.id)
      if (!existingItem) {
        this.cartItems.push({
          medicine: medicine,
          quantity: 1
        })     
        
        this.updateCartInSession()
        this.showToastMessage(`${medicine.name} added to cart`, 'success')
      }
    },
    
    updateCartInSession() {
      const verifiedCartItems = this.cartItems.map(item => {
        if (!item.medicine.tp_amount || isNaN(parseFloat(item.medicine.tp_amount))) {
          item.medicine.tp_amount = "0.00"
        }
        
        item.medicine.price = parseFloat(item.medicine.tp_amount || 0)
        return item
      })
      
      sessionStorage.setItem('cartItems', JSON.stringify(verifiedCartItems))
    }
  },
  
  created() {
    this.loadUserData()
    if (!this.checkAuthStatus()) {
      return
    }
    
    let pharmacyFromState = this.$route.state?.pharmacy || {}
    const routePharmacyId = this.$route.params.pharmacyId
    const routePharmacyName = this.$route.params.pharmacyName 
    
    this.localSelectedPharmacy = { 
      ...this.selectedPharmacy,
      ...pharmacyFromState,
      id: pharmacyFromState.id || routePharmacyId || this.selectedPharmacy.id,
      name: pharmacyFromState.name || routePharmacyName || this.selectedPharmacy.name,
      address: pharmacyFromState.address || this.selectedPharmacy.address
    }
    
    console.log('Current route:', this.$route.path)
    console.log('Pharmacy info:', this.localSelectedPharmacy)
    console.log('User info:', this.currentUser)
    
    document.title = `Medicine List - ${routePharmacyName}`
    
    this.fetchMedicines()
    
    // Load cart items from session storage
    const storedCartItems = sessionStorage.getItem('cartItems')
    if (storedCartItems) {
      try {
        this.cartItems = JSON.parse(storedCartItems)
        console.log('Restored cart items from session:', this.cartItems.length)
      } catch (e) {
        console.error('Error parsing stored cart items:', e)
      }
    }

    // Add event listener to close dropdowns when clicking outside
    document.addEventListener('click', this.closeDropdowns)
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    
    // Listen for storage changes to keep cart in sync
    window.addEventListener('storage', (e) => {
      if (e.key === 'cartItems') {
        const storedCartItems = sessionStorage.getItem('cartItems')
        if (storedCartItems) {
          try {
            this.cartItems = JSON.parse(storedCartItems)
          } catch (error) {
            console.error('Error parsing stored cart items:', error)
          }
        }
      }
    })
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    document.removeEventListener('click', this.closeDropdowns)
  },
  
  watch: {
    searchQuery() {
      this.displayCount = 20
    }
  }
}
</script>

<!-- MedicineList.vue's css -->

<style scoped>
.medicine-list-view {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  background-color: #000;
  color: #e2e2e2;
  max-width: 480px;
  margin: 0 auto;
  min-height: 100vh;
  position: relative;
  padding-bottom: 80px;
}

/* Header and Menu Bar Styles */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  position: sticky;
  top: 0;
  background-color: #000;
  border-radius: 8px;
  z-index: 10;
  border-bottom: 1px solid #333;
}

.header h1 {
  margin: 0;
  color: #3aa757;
  font-size: 18px;
  flex: 1;
}

.menu-bar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.menu-item {
  position: relative;
}

/* Cart specific styles */
.cart-menu {
  margin-right: 10px;
}

.cart-icon-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  cursor: pointer;
  border-radius: 50%;
  background-color: rgba(58, 167, 87, 0.2);
  transition: background-color 0.3s ease;
}

.cart-icon-container:hover {
  background-color: #3aa757;
}

.cart-icon {
  width: 18px;
  height: 18px;
  color: #3aa757;
}

.cart-icon:hover {
  color: #e2e2e2;
}

.cart-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  min-width: 18px;
}

/* User Avatar Styles */
.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  /* border: 2px solid rgba(58, 167, 87, 0.3); */
  transition: border-color 0.3s ease;
}

.user-avatar:hover {
  border-color: #e9ecef;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Dropdown Menu Styles */
.dropdown-menu {
  position: absolute;
  top: calc(100% + 10px);
  background: #1a1a1a;
  border: 1px solid #444;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  min-width: 180px;
  overflow: hidden;
}

.menu-dropdown {
  right: 0;
}

/* Enhance cart dropdown */
.cart-dropdown {
  width: 350px;
  right: 0;
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  border: 1px solid #03a003;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
}
.dropdown-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e2e2e2;
  font-size: 14px;
}

.dropdown-item:hover {
  background-color: #333;
}

/* Cart Dropdown Styles */
.cart-empty {
  padding: 20px;
  text-align: center;
  color: #ccc;
}

.cart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #444;
}

.cart-header h4 {
  margin: 0;
  font-size: 14px;
  color: #e2e2e2;
}

.cart-total {
  font-weight: bold;
  color: #3aa757;
}

.cart-items-list {
  max-height: 300px;
  overflow-y: auto;
}

.cart-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #333;
}

.cart-item:last-child {
  border-bottom: none;
}

.cart-item-info {
  flex: 1;
}

.medicine-name {
  display: block;
  font-weight: 500;
  color: #e2e2e2;
  margin-bottom: 4px;
  font-size: 14px;
}

.cart-item-details {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #ccc;
}

.remove-item {
  background: none;
  border: none;
  color: #fd1900;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  transition: background-color 0.3s ease;
}

.remove-item:hover {
  background-color: #e2e2e298;
}

.cart-actions {
  display: flex;
  gap: 10px;
  padding: 15px;
  border-top: 1px solid #444;
}

.clear-cart-btn, .checkout-btn {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  font-size: 11px;
}

.clear-cart-btn {
  background-color: #e74c3c;
  color: white;
}

.clear-cart-btn:hover {
  background-color: #c0392b;
}

.checkout-btn {
  background-color: #3aa757;
  color: white;
}

.checkout-btn:hover {
  background-color: #218838;
}

/* Toast message styles */
.toast-message {
  position: fixed;
  top: 80px;
  right: 20px;
  padding: 12px 20px;
  border-radius: 4px;
  z-index: 1000;
  max-width: 350px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.5);
  animation: slideIn 0.3s ease-out;
}

.toast-message.info {
  background-color: #333;
  color: #e2e2e2;
  border: 1px solid #555;
}

.toast-message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.toast-message.warning {
  background-color: #e2e2e2;
  color: #856404;
  border: 1px solid #ffeaa7;
}


.toast-message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Search Bar */
.search-bar {
  padding: 0 14px 0px;
}

.search-bar input {
  width: 100%;
  padding: 10px;
  border-radius: 4px;
  border: none;
  background-color: #333;
  color: #e2e2e2;
  box-sizing: border-box;
}

/* Medicine Grid */
.medicine-grid {
  padding: 8px;
}

.medicine-card {
  display: flex;
  background-color: #1a1a1a;
  border: 1px solid #444;
  border-radius: 7px;
  margin: 5px;
  padding: 5px 5px 5px 2px;
  gap: 0.7rem;
  transition: all 0.3s ease;
}

/* Highlight medicine cards that are in cart */
.medicine-card.in-cart {
  border-color: #33f543;
  box-shadow: 0 0 10px rgba(58, 167, 87, 0.3);
}

.medicine-img {
  width: 60px;
  height: 60px;
  overflow: hidden;
  border-radius: 4px;
  margin-right: 8px;
  background-color: #333;
}

.placeholder-img {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
}

.medicine-info {
  flex: 1;
  transform: translateY(-5px);
}

.medicine-subinfo {
  color: #c7c09c;
  font-size: 11px;
  font-style: italic;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 190px;
  display: inline-block;
}

.medicine-info h3 {
  display: flex;
  margin: 0 0 6px 0;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 230px;
}

.medicine-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 11px;
}

.price {
  font-weight: bold;
  color: #3aa757;
  margin-left: auto;
}

.medicine-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.left-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.medicine_quantity,.medicine_quantity_details {
  font-size: 11px;
  margin: 0;
  padding: 0 2px;
  text-align: center;
  color: #ccc;
}

.medicine_quantity_details {
  font-size: 11px;
  color: #999;
  text-align: left;
  transform: translateY(0);
}

.medicine-actions > * {
  margin-top: -5px;
}

.quantity-selector {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  height: 22px;
}

.quantity-selector button {
  width: 22px;
  height: 22px;
  background-color: #333;
  color: #e2e2e2;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

/* Delete button styles */
.delete-button {
  width: 22px !important;
  height: 20px !important;
  background-color: #e74c3c !important;
  color: #e2e2e2 !important;
  border: none !important;
  border-radius: 6px !important;
  font-size: 11px !important;
  font-weight: bold !important;
  cursor: pointer !important;
  transition: background-color 0.3s, transform 0.1s !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin-left: 2px !important;
}

.delete-button:hover {
  background-color: #c0392b !important;
  transform: scale(1.1) !important;
}

.delete-button:active {
  transform: scale(0.95) !important;
}

/* Quantity input field styles */
.quantity-input {
  width: 35px;
  height: 22px;
  background-color: #444;
  color: #e2e2e2;
  border: 1px solid #666;
  border-radius: 4px;
  text-align: center;
  font-size: 11px;
  padding: 0;
  -moz-appearance: textfield; /* Firefox */
}

/* Hide spinner arrows for webkit browsers */
.quantity-input::-webkit-outer-spin-button,
.quantity-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.quantity-input:focus {
  outline: none;
  border-color: #3aa757;
  background-color: #555;
}

.add-to-cart {
  background-color: #3aa757;
  color: #e2e2e2;
  border: 1px solid #3aa757;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Cart icon styles */
.cart-icon {
  color: #e2e2e2;
  width: 16px;
  height: 16px;
}

.add-to-cart:hover {
  background-color: #61f57ae3;
  border-color: #e2e2e2;
  color: #e2e2e2;
}

.no-medicines, .loading-message, .error-message {
  padding: 40px 16px;
  text-align: center;
  color: #ccc;
}

.loading-more {
  padding: 20px 16px;
  text-align: center;
  color: #ccc;
  font-size: 14px;
}

.load-all-button {
  background-color: #3aa757;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  margin-top: 16px;
  margin-right: 5px;
  font-size: 14px;
  cursor: pointer;
}

.add-item-button {
  background-color: #3aa757;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  margin-top: 16px;
  margin-left: 5px;
  font-size: 14px;
  cursor: pointer;
}

.medicine_quantity {
  font-size: 11px;
  color: #ccc;
  padding-bottom: 10px;
  text-align: center;
  transform: translateY(5px);
}

/* Footer */
.footer {
  padding: 20px 16px;
  text-align: center;
  color: #666;
  font-size: 11px;
  border-top: 1px solid #333;
  margin-top: 40px;
}

/* Responsive adjustments for smaller screens */
@media (max-width: 480px) {
  .cart-dropdown {
    width: 300px;
    right: -10px;
  }
  
  .toast-message {
    right: 10px;
    max-width: 300px;
  }
  
  .header {
    padding: 12px;
  }
  
  .search-bar {
    padding: 0 12px;
  }
  
  .medicine-grid {
    padding: 6px;
  }
}
</style>